import * as THREE from 'three';

/** Camera view modes (spec §7). */
export enum CameraViewMode {
  Perspective = 'perspective',
  Top = 'top',
  Presentation = 'presentation',
}

/**
 * Pure orbit state describing the camera relative to a look-at target. Kept free of Three.js input
 * handling so the math is unit-testable (spec §1.4 "unit-test camera-state math").
 */
export interface OrbitState {
  target: THREE.Vector3;
  /** Distance from target in world meters. */
  distance: number;
  /** Horizontal angle in radians (around +Y). */
  azimuth: number;
  /** Vertical angle in radians from +Y axis (0 = top-down, PI/2 = horizon). */
  polar: number;
}

export interface OrbitClamps {
  minDistance: number;
  maxDistance: number;
  minPolar: number;
  maxPolar: number;
}

export const DEFAULT_ORBIT_CLAMPS: OrbitClamps = {
  minDistance: 2,
  maxDistance: 400,
  // Keep the camera above the ground and below straight-down to avoid gimbal issues.
  minPolar: 0.05,
  maxPolar: Math.PI / 2 - 0.02,
};

/** Clamp an orbit state's distance and polar angle in place, returning the same object. */
export function clampOrbitState(state: OrbitState, clamps: OrbitClamps): OrbitState {
  state.distance = THREE.MathUtils.clamp(state.distance, clamps.minDistance, clamps.maxDistance);
  state.polar = THREE.MathUtils.clamp(state.polar, clamps.minPolar, clamps.maxPolar);
  return state;
}

/** Convert an orbit state to a world-space camera position. Pure. */
export function orbitStateToPosition(state: OrbitState, out = new THREE.Vector3()): THREE.Vector3 {
  const sinPolar = Math.sin(state.polar);
  out.set(
    state.target.x + state.distance * sinPolar * Math.sin(state.azimuth),
    state.target.y + state.distance * Math.cos(state.polar),
    state.target.z + state.distance * sinPolar * Math.cos(state.azimuth),
  );
  return out;
}

/* ---------------------------------- The gesture math ---------------------------------- */

/**
 * The screen plane in world space: what "right" and "up" mean to someone looking through this
 * camera. Panning needs both, because a drag should move the site the way the cursor moved rather
 * than the way the ground happens to lie under it.
 */
export interface ScreenBasis {
  right: THREE.Vector3;
  up: THREE.Vector3;
}

export function orbitScreenBasis(
  state: OrbitState,
  right = new THREE.Vector3(),
  up = new THREE.Vector3(),
): ScreenBasis {
  const sinA = Math.sin(state.azimuth);
  const cosA = Math.cos(state.azimuth);
  const sinP = Math.sin(state.polar);
  const cosP = Math.cos(state.polar);
  right.set(cosA, 0, -sinA);
  up.set(-cosP * sinA, sinP, -cosP * cosA);
  return { right, up };
}

/** Radians of turn per pixel of drag — a little over a half-turn across a 720 px viewport. */
export const ORBIT_RADIANS_PER_PIXEL = 0.005;

/**
 * Turn the camera around its target by a pointer delta in pixels.
 *
 * The site follows the cursor: drag right and it swings right, drag down and it tips its top toward
 * you. That is the direction SketchUp, three.js and every CAD orbit agree on, and it is the reason
 * the gesture reads as *handling* the model rather than steering a camera around it.
 */
export function rotateOrbitState(
  state: OrbitState,
  dxPixels: number,
  dyPixels: number,
  radiansPerPixel = ORBIT_RADIANS_PER_PIXEL,
): OrbitState {
  state.azimuth -= dxPixels * radiansPerPixel;
  state.polar -= dyPixels * radiansPerPixel;
  return state;
}

/**
 * Slide the view by a pointer delta in pixels.
 *
 * SketchUp's pan is a true screen-space one — the point you grabbed stays under the cursor at any
 * tilt — so the target travels along the screen plane rather than along the ground. Sliding it
 * along the ground instead, as MapControls does, is the cheaper thing and it is what was here
 * before: it drifts away from the cursor as the view flattens toward the horizon, and the vertical
 * axis was inverted on top of that, so a drag downward hauled the site upward.
 *
 * `worldPerPixel` is what one pixel is worth at the target's depth; see
 * {@link CameraManager.worldUnitsPerPixel}. `basis` defaults to the orbit's own screen plane, which
 * is right for the perspective view and wrong for the top one — that camera is pinned north-up and
 * ignores the azimuth, so a straight drag would come out diagonal. Callers with a live camera pass
 * {@link CameraManager.screenBasis} instead.
 */
export function panOrbitState(
  state: OrbitState,
  dxPixels: number,
  dyPixels: number,
  worldPerPixel: number,
  basis: ScreenBasis = orbitScreenBasis(state),
): OrbitState {
  const { right, up } = basis;
  // The site follows the cursor, so the camera goes the other way: drag right and the target moves
  // left, which leaves what was under the pointer still under it.
  state.target.addScaledVector(right, -dxPixels * worldPerPixel);
  state.target.addScaledVector(up, dyPixels * worldPerPixel);
  return state;
}

/**
 * Move the camera in or out by a factor, optionally holding one world point fixed on screen.
 *
 * Zooming about a pivot is the whole trick of SketchUp's wheel: whatever is under the cursor stays
 * under the cursor, so you steer by pointing instead of alternating zoom and pan. Scaling the whole
 * rig — eye and target together — about that point leaves both angles untouched and changes only
 * the distance, so this stays an orbit state rather than quietly becoming a free camera.
 *
 * Returns the factor actually applied, which falls short of the one asked for once the distance
 * clamp bites. The caller needs it: at the limit the pivot must stop moving too, or the ground
 * would keep sliding under a cursor that is no longer zooming anything.
 */
export function dollyOrbitState(
  state: OrbitState,
  factor: number,
  clamps: OrbitClamps,
  pivot?: THREE.Vector3,
): number {
  const before = state.distance;
  state.distance = THREE.MathUtils.clamp(before * factor, clamps.minDistance, clamps.maxDistance);
  const applied = before > 0 ? state.distance / before : 1;
  if (pivot) {
    state.target.sub(pivot).multiplyScalar(applied).add(pivot);
  }
  return applied;
}

/* ------------------------------------- The lens ------------------------------------- */

/**
 * The lens, quoted the way a phone quotes it.
 *
 * A field of view in degrees is the wrong number to put in front of a designer: nobody composes in
 * degrees, and "50" says nothing about whether the shot will read as a wide sweep or a compressed
 * detail. Every camera the user already owns says `0.5×`, `1×`, `2×` instead, so the app does too —
 * a multiplier against the lens the view has always had, which is 26 mm in 35 mm-equivalent terms
 * and therefore already the `1×` a phone means by it.
 *
 * The conversion is the ordinary one: half the 35 mm frame's height over the focal length is the
 * tangent of half the *vertical* field of view, and vertical is what Three.js takes.
 */
export const DEFAULT_FOCAL_MM = 26;
export const MIN_LENS_ZOOM = 0.5;
export const MAX_LENS_ZOOM = 2;
/** Half the height of a 35 mm frame, the reference an equivalent focal length is quoted against. */
const FRAME_HALF_HEIGHT_MM = 12;

export function clampLensZoom(zoom: number): number {
  if (!Number.isFinite(zoom)) return 1;
  return THREE.MathUtils.clamp(zoom, MIN_LENS_ZOOM, MAX_LENS_ZOOM);
}

/** The 35 mm-equivalent focal length a zoom multiplier stands for. */
export function focalMmForZoom(zoom: number): number {
  return DEFAULT_FOCAL_MM * clampLensZoom(zoom);
}

/** Vertical field of view, in degrees, for a zoom multiplier. */
export function fovDegreesForZoom(zoom: number): number {
  return THREE.MathUtils.radToDeg(2 * Math.atan(FRAME_HALF_HEIGHT_MM / focalMmForZoom(zoom)));
}

/** The inverse: what a stored field of view means as a zoom multiplier. Clamped to the range. */
export function zoomForFovDegrees(fovDegrees: number): number {
  if (!Number.isFinite(fovDegrees) || fovDegrees <= 0 || fovDegrees >= 180) return 1;
  const focal = FRAME_HALF_HEIGHT_MM / Math.tan(THREE.MathUtils.degToRad(fovDegrees) / 2);
  return clampLensZoom(focal / DEFAULT_FOCAL_MM);
}

export interface CameraManagerOptions {
  aspect?: number;
  clamps?: OrbitClamps;
}

/**
 * Manages the perspective and orthographic cameras and the current view mode. Input handling lives
 * in {@link CameraController}; this class owns state and applies it to the active camera.
 */
export class CameraManager {
  readonly perspective: THREE.PerspectiveCamera;
  readonly orthographic: THREE.OrthographicCamera;
  readonly clamps: OrbitClamps;

  private mode: CameraViewMode = CameraViewMode.Perspective;
  private aspect: number;
  /** The lens, as a multiplier of the default 26 mm. See {@link setLensZoom}. */
  private lens = 1;
  readonly orbit: OrbitState = {
    target: new THREE.Vector3(0, 0, 0),
    distance: 60,
    azimuth: Math.PI * 0.25,
    polar: Math.PI * 0.32,
  };

  constructor(options: CameraManagerOptions = {}) {
    this.aspect = options.aspect ?? 1;
    this.clamps = options.clamps ?? DEFAULT_ORBIT_CLAMPS;
    this.perspective = new THREE.PerspectiveCamera(fovDegreesForZoom(this.lens), this.aspect, 0.1, 2000);
    this.orthographic = new THREE.OrthographicCamera(-50, 50, 50, -50, 0.1, 2000);
    this.applyOrbit();
  }

  get activeCamera(): THREE.Camera {
    return this.mode === CameraViewMode.Top ? this.orthographic : this.perspective;
  }

  get viewMode(): CameraViewMode {
    return this.mode;
  }

  setViewMode(mode: CameraViewMode): void {
    this.mode = mode;
    if (mode === CameraViewMode.Presentation) {
      // Lower, cinematic angle looking slightly up toward the horizon.
      this.orbit.polar = Math.min(this.clamps.maxPolar, Math.PI * 0.46);
    }
    this.applyOrbit();
  }

  /** The lens in force, as a multiplier of the default. `1` is what the view opens on. */
  get lensZoom(): number {
    return this.lens;
  }

  /** The same lens as a 35 mm-equivalent focal length, for anything that talks in millimetres. */
  get focalLengthMm(): number {
    return focalMmForZoom(this.lens);
  }

  get fovDegrees(): number {
    return this.perspective.fov;
  }

  /**
   * Put the camera on a different lens, without moving it.
   *
   * Zooming with the lens is not the same gesture as orbiting closer, and the difference is the
   * whole reason this exists: standing back with a long lens flattens a garden into the layered
   * reading a plan wants, while walking in with the wide one bows the beds outward. Both are
   * reachable now, from the same spot.
   *
   * Top view has no lens to speak of — an orthographic camera has no field of view — so the
   * multiplier is applied to its frustum instead. That keeps `2×` meaning "twice as close" in every
   * view rather than silently doing nothing in one of them.
   */
  setLensZoom(zoom: number): void {
    this.lens = clampLensZoom(zoom);
    this.perspective.fov = fovDegreesForZoom(this.lens);
    this.perspective.updateProjectionMatrix();
    this.updateOrthoFrustum();
  }

  /**
   * The screen plane of the camera actually in force, read off its own orientation.
   *
   * Pan needs this rather than {@link orbitScreenBasis} because the two cameras do not agree: the
   * top view is pinned north-up and looking straight down whatever the orbit's azimuth says, so
   * deriving its basis from the orbit state would send a straight drag off on a diagonal. Taken
   * from the quaternion rather than the world matrix, because `lookAt` has already settled the
   * quaternion by the time this is asked and the matrix only catches up at the next render.
   */
  screenBasis(right = new THREE.Vector3(), up = new THREE.Vector3()): ScreenBasis {
    const { quaternion } = this.activeCamera;
    right.set(1, 0, 0).applyQuaternion(quaternion);
    up.set(0, 1, 0).applyQuaternion(quaternion);
    return { right, up };
  }

  /**
   * What one pixel of pointer travel is worth in metres, at the depth of the orbit target.
   *
   * Pan is the caller that needs it, and it has to come from the live projection rather than a
   * tuned constant: a longer lens narrows the frame without moving the camera, and the top view has
   * no field of view at all, so a fixed pixels-to-metres rate would leave the ground sliding out
   * from under the cursor in exactly the two cases a designer works in most.
   */
  worldUnitsPerPixel(viewportHeightPixels: number): number {
    if (!(viewportHeightPixels > 0)) return 0;
    if (this.mode === CameraViewMode.Top) {
      return (this.orthographic.top - this.orthographic.bottom) / viewportHeightPixels;
    }
    const halfFov = THREE.MathUtils.degToRad(this.perspective.fov) / 2;
    return (2 * this.orbit.distance * Math.tan(halfFov)) / viewportHeightPixels;
  }

  setAspect(aspect: number): void {
    this.aspect = aspect;
    this.perspective.aspect = aspect;
    this.perspective.updateProjectionMatrix();
    this.updateOrthoFrustum();
  }

  /** Apply the current orbit state to both cameras (after clamping). */
  applyOrbit(): void {
    clampOrbitState(this.orbit, this.clamps);
    const pos = orbitStateToPosition(this.orbit);
    this.perspective.position.copy(pos);
    this.perspective.lookAt(this.orbit.target);
    this.perspective.updateProjectionMatrix();

    // Top view: orthographic camera directly above the target looking down.
    this.orthographic.position.set(
      this.orbit.target.x,
      this.orbit.target.y + Math.max(this.orbit.distance, this.clamps.minDistance),
      this.orbit.target.z,
    );
    this.orthographic.up.set(0, 0, -1);
    this.orthographic.lookAt(this.orbit.target);
    this.updateOrthoFrustum();
  }

  private updateOrthoFrustum(): void {
    const halfH = (this.orbit.distance * 0.5) / this.lens;
    const halfW = halfH * this.aspect;
    this.orthographic.left = -halfW;
    this.orthographic.right = halfW;
    this.orthographic.top = halfH;
    this.orthographic.bottom = -halfH;
    this.orthographic.updateProjectionMatrix();
  }

  /** Frame the camera target on a world point at a given distance. */
  focusOn(point: THREE.Vector3, distance?: number): void {
    this.orbit.target.copy(point);
    if (distance !== undefined) {
      this.orbit.distance = distance;
    }
    this.applyOrbit();
  }
}
